import { useQuery } from '@apollo/client';
import styled, { useTheme } from 'styled-components';
import { useCallback, useMemo, useState } from 'react';

import {
  CurrencyType,
  TransactionStatusType,
  TransactionType,
} from '@libs/types';
import { formatDate, useApiResponse, useDimensions } from '@libs/hooks';
import {
  AssetNetworkIcon,
  LongArrowIcon,
  Typography,
  useStatusInfo,
} from '@libs/components';
import { convertExponentialToDecimal, formatCryptoAmount } from '@libs/helpers';
import { screenSizes } from '@libs/theme';

import { TableCell } from 'src/components';
import { useTranslation } from 'src/hooks';
import { TRANSACTION_LABELS, USDT_PRICE } from 'src/constants';
import { TransactionAppliedFilters } from 'src/types';
import { getAlternativeAssetLabel } from 'src/helpers';
import {
  getRecentTransactionsQuery,
  GetRecentTransactionsRes,
  getUserTransactionsGroupedByDateQuery,
  GetUserTransactionsGroupedByDateRes,
} from 'src/gql';

interface UserDataType {
  data: Record<string, JSX.Element>[];
  mobileData: Record<string, JSX.Element>[];
  mobileOneAssetData: Record<string, JSX.Element>[];
}

export const useTransactionsTableGe = ({
  skip = 0,
  take,
  appliedFilters,
  onRowClick,
  isOneAssetTransactions,
}: {
  skip?: number;
  take: number;
  appliedFilters?: TransactionAppliedFilters;
  onRowClick?: (n: number) => void;
  isOneAssetTransactions?: boolean;
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [dataIsLoaded, setDataIsLoaded] = useState(false);
  const { getStatusData } = useStatusInfo();
  const { width } = useDimensions();

  const onTransactionSuccess = (res: GetUserTransactionsGroupedByDateRes) => {
    if (res?.getUserTransactionsGroupedByDate) {
      setDataIsLoaded(true);
    }
  };
  const onTransactionOldSuccess = (res: GetRecentTransactionsRes) => {
    if (res?.getRecentTransactions) {
      setDataIsLoaded(true);
    }
  };

  const filterdStatuses = useMemo(() => {
    return appliedFilters?.statuses.reduce((acc, status) => {
      if (status === TransactionStatusType.PENDING) {
        acc.push(
          TransactionStatusType.PENDING,
          TransactionStatusType.DRAFT,
          TransactionStatusType.PENDING_MANUAL_REVIEW,
          TransactionStatusType.PROCESSING
        );
      } else {
        acc.push(status);
      }
      return acc;
    }, [] as TransactionStatusType[]);
  }, [appliedFilters]);

  const { data: transactionsDataOld, loading: transactionIsLoadingOld } =
    useQuery(getRecentTransactionsQuery, {
      variables: {
        record: {
          skip,
          take,
          ...appliedFilters,
          statuses: filterdStatuses,
        },
      },
      skip: width < screenSizes.tablet,
      fetchPolicy: 'network-only',
    });

  const { response: transactionsOld, loading: loadingOldTransactions } =
    useApiResponse(
      {
        data: transactionsDataOld,
        loading: transactionIsLoadingOld,
      },
      onTransactionOldSuccess
    );

  const { data: transactionsData, loading: transactionIsLoading } = useQuery(
    getUserTransactionsGroupedByDateQuery,
    {
      variables: {
        record: {
          skip,
          take,
          ...appliedFilters,
          statuses: filterdStatuses,
        },
      },
      skip: width > screenSizes.tablet,
      fetchPolicy: 'network-only',
    }
  );

  const { response: transactions, loading } = useApiResponse(
    {
      data: transactionsData,
      loading: transactionIsLoading,
    },
    onTransactionSuccess
  );

  const columns = useMemo(() => {
    return [
      ...(!isOneAssetTransactions
        ? [
            {
              Header: `${t('table_coin')}/${t('table_type')}`,
              accessor: 'coin',
            },
          ]
        : []),
      { Header: t('table_date'), accessor: 'date' },
      { Header: t('table_amount'), accessor: 'amount' },
      { Header: t('table_fee'), accessor: 'fee' },
      { Header: t('table_operation_id'), accessor: 'operationId' },
      { Header: t('table_status'), accessor: 'status' },
    ];
  }, [isOneAssetTransactions, t]);

  const mobileOneAssetColumns = useMemo(
    () => [
      { Header: t('table_date'), accessor: 'date' },
      { Header: t('table_amount'), accessor: 'amount' },
      { Header: t('table_status'), accessor: 'status' },
    ],
    [t]
  );

  const userData: UserDataType = useMemo(() => {
    // Combine and flatten both data sources.
    const combinedTransactions = [
      ...(transactions?.getUserTransactionsGroupedByDate.flatMap((group) =>
        group.transactions.map((tx) => ({
          ...tx,
          // Use the group's createdAt if transaction's createdAt is not available
          createdAt: tx.createdAt || group.createdAt,
        }))
      ) || []),
      ...(transactionsOld?.getRecentTransactions || []),
    ];

    if (!combinedTransactions.length)
      return { data: [], mobileData: [], mobileOneAssetData: [] };

    return combinedTransactions.reduce(
      (acc, transaction) => {
        const {
          assetCode: code,
          type,
          amount,
          fromAmount,
          fee,
          fromAsset,
          toAsset,
          toAmount,
          feeAssetCode,
        } = transaction.details;
        const date = new Date(transaction.createdAt);
        const assetCode = code;

        const fromAssetCode = fromAsset?.code
          ? getAlternativeAssetLabel(fromAsset.code as CurrencyType)
          : '';
        const toAssetCode = toAsset?.code
          ? getAlternativeAssetLabel(toAsset.code as CurrencyType)
          : '';
        const transactionStatus =
          transaction.status === TransactionStatusType.DRAFT
            ? getStatusData(TransactionStatusType.PENDING)
            : getStatusData(transaction.status);

        const feeToShow =
          type === TransactionType.BonusClaim
            ? '0'
            : convertExponentialToDecimal(fee);

        const feeAssetCodeToShow = getAlternativeAssetLabel(
          type === TransactionType.BonusClaim ? assetCode : feeAssetCode
        );

        const amountToShow =
          type === TransactionType.BonusClaim
            ? formatCryptoAmount(amount, USDT_PRICE, 2)
            : amount;
        const fromAmountToShow =
          type === TransactionType.BonusClaim && fromAmount
            ? formatCryptoAmount(fromAmount, USDT_PRICE, 2)
            : fromAmount;
        const toAmountToShow =
          type === TransactionType.BonusClaim && toAmount
            ? formatCryptoAmount(toAmount, USDT_PRICE, 2)
            : toAmount;

        const assetSubCode =
          CurrencyType.GEL === transaction.details.assetCode ||
          CurrencyType.USD === transaction.details.assetCode
            ? ''
            : transaction.details.assetCode;

        acc.data.push({
          coin: (
            <FlexCenter>
              <IconWrapper>
                <CustomAssetNetworkIcon
                  isAlone={!transaction.details.toAsset?.iconUrl}
                  baseUrl={
                    transaction.details.fromAsset?.iconUrl ??
                    transaction.details.assetIconUrl
                  }
                  networkIcon={transaction.details.toAsset?.iconUrl}
                />
              </IconWrapper>
              <FlexColumn>
                <FlexCenter>
                  <Typography type="subHeadline" fontFamily="primaryMedium">
                    {transaction.details.fromAsset?.name
                      ? transaction.details.fromAsset.name
                      : transaction.details.assetLabel}
                  </Typography>
                  {!transaction.details.toAsset?.name && (
                    <CodeTypography
                      type="bodyButton"
                      fontFamily="primaryRegular"
                      color={colors.text.secondary}
                    >
                      {assetSubCode}
                    </CodeTypography>
                  )}
                  {transaction.details.toAsset?.name && (
                    <FlexCenter>
                      <ArrowWrapper>
                        <LongArrowIcon />
                      </ArrowWrapper>
                      <Typography type="subHeadline" fontFamily="primaryMedium">
                        {transaction.details.toAsset?.name}
                      </Typography>
                    </FlexCenter>
                  )}
                </FlexCenter>
                <Typography
                  type="caption"
                  fontFamily="primaryRegular"
                  color={colors.text.secondary}
                >
                  {t(TRANSACTION_LABELS[type])}
                </Typography>
              </FlexColumn>
            </FlexCenter>
          ),
          date: (
            <TableCell
              title={formatDate('D MMM YYYY', date)}
              titleType="primaryMedium"
              titleSize="bodyButton"
              description={formatDate('HH:mm', date)}
            />
          ),
          amount: fromAmountToShow ? (
            <FlexColumn>
              <Typography type="bodyButton" fontFamily="primaryMedium">
                {`${toAmountToShow} `}
                {toAssetCode}
              </Typography>

              <Typography
                type="caption"
                fontFamily="primaryMedium"
                color={colors.text.secondary}
              >
                {`-${fromAmountToShow} `}
                {fromAssetCode}
              </Typography>
            </FlexColumn>
          ) : (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {`${amountToShow} `}
              {assetCode}
            </Typography>
          ),

          fee: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {feeToShow} {feeAssetCodeToShow}
            </Typography>
          ),
          operationId: (
            <Typography type="bodyButton" fontFamily="primaryMedium">
              {transaction.id}
            </Typography>
          ),
          status: (
            <Typography
              type="bodyButton"
              fontFamily="primaryMedium"
              color={transactionStatus.colors[0]}
            >
              {t(transactionStatus.text)}
            </Typography>
          ),
        });

        acc.mobileOneAssetData.push({
          date: (
            <TableCell
              title={formatDate('D MMM YYYY', date)}
              titleType="primaryMedium"
              // description={formatDate('HH:mm', date)}
            />
          ),
          amount: (
            <>
              <Typography type="bodyButton" fontFamily="primaryMedium">
                {`${amountToShow} `}
              </Typography>
              <Typography
                type="caption"
                fontFamily="primaryMedium"
                color={colors.text.secondary}
              >
                {assetCode}
              </Typography>
            </>
          ),
          status: (
            <TableCell
              title={t(TRANSACTION_LABELS[type])}
              justifyToLeft={false}
              description={t(transactionStatus.text)}
              color={transactionStatus.colors[0]}
            />
          ),
        });

        return acc;
      },
      { data: [], mobileData: [], mobileOneAssetData: [] } as UserDataType
    );
  }, [transactions, transactionsOld, t, getStatusData, colors]);

  const handleRowClick = useCallback(
    (index: number) => {
      // Flatten transactions first to get the correct index
      const allTransactions = [
        ...(transactions?.getUserTransactionsGroupedByDate.flatMap(
          (group) => group.transactions
        ) || []),
        ...(transactionsOld?.getRecentTransactions || []),
      ];
      onRowClick?.(allTransactions[index]?.id || 0);
    },
    [onRowClick, transactions, transactionsOld]
  );

  return {
    columns,
    data: userData.data,

    mobileOneAssetColumns,
    mobileOneAssetData: userData.mobileOneAssetData,
    handleRowClick,
    dataIsLoaded,
    loading: loading || loadingOldTransactions,
    transactions: transactions?.getUserTransactionsGroupedByDate,
  };
};

export const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FlexCenter = styled.div`
  display: flex;
  align-items: center;
`;
export const CodeTypography = styled(Typography)`
  margin-left: 0.4rem;
`;
export const ArrowWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0 0.4rem;
`;
type AssetNetworkIconProps = {
  isAlone?: boolean;
};
export const CustomAssetNetworkIcon = styled(
  AssetNetworkIcon
)<AssetNetworkIconProps>`
  margin-left: ${({ isAlone }) => (isAlone ? '0' : '-1.1rem')};
  .baseNetworkIcon {
    width: ${({ isAlone }) => (isAlone ? '4.2rem' : '2.2rem ')};
    height: ${({ isAlone }) => (isAlone ? '4.2rem' : '2.2rem ')};
  }
  .networkIconWrapper {
    width: 2.2rem;
    height: 2.2rem;
    bottom: -1rem;
    right: -1.1rem;
    top: unset;
    padding: 0;
  }
`;
export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4.2rem;
  height: 4.2rem;
  margin-right: 1.1rem;
`;
